import React from 'react';
import {
  dashboardAlert,
  dashboardMuted,
  dashboardPage,
  dashboardStatCard,
  dashboardStatLabel,
  utcDataDisclaimer,
} from '../../theme/dashboardLayout';
import { forecastHorizons } from './forecasts/forecastHelpers';
import PulseHero from './hero/PulseHero';
import { COHORT_SUPPRESSED_COPY } from './mapResponses';
import { PopulationPulseDashboardResult } from './types';
import ReadyDashboard from './view/ReadyDashboard';

interface Props {
  result: PopulationPulseDashboardResult;
}

function PulseLockedTease(): React.ReactElement {
  return (
    <div
      data-testid="pulse-locked-tease"
      aria-hidden="true"
      className="relative overflow-hidden rounded-xl bg-portal-header-wash/60"
    >
      <div className="pointer-events-none select-none blur-sm opacity-50">
        <div className="flex h-24 items-end gap-2 px-4 pb-3">
          {[40, 65, 50, 80, 55, 70, 45].map((height, index) => (
            <div
              key={index}
              className="flex-1 rounded-t bg-portal-teal/30"
              style={{ height: `${height}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PopulationPulseView({ result }: Props): React.ReactElement {
  if (result.status !== 'ready') {
    return (
      <div className={dashboardPage}>
        <PulseHero
          enrolledMembers={result.enrolledMembersCount ?? undefined}
        />
        {result.status === 'suppressed' ? (
          <article className={dashboardStatCard}>
            <PulseLockedTease />
            <p className="mt-4 text-p1 text-portal-teal/80 leading-relaxed">
              {COHORT_SUPPRESSED_COPY}
            </p>
          </article>
        ) : (
          <article className={dashboardStatCard}>
            <h2 className={dashboardStatLabel}>Unavailable</h2>
            <p className={`mt-3 ${dashboardAlert}`}>{result.message}</p>
          </article>
        )}
        <p className={`mt-8 ${dashboardMuted}`}>{utcDataDisclaimer}</p>
      </div>
    );
  }

  const { momentum, forecast } = result;
  const participation = momentum.signal_participation;
  const forecasts = forecastHorizons(forecast, momentum);

  return (
    <ReadyDashboard
      momentum={momentum}
      participation={participation}
      forecasts={forecasts}
      disclaimer={forecast.disclaimer || momentum.disclaimer}
      enrolledMembers={result.enrolledMembersCount ?? undefined}
    />
  );
}

export default PopulationPulseView;
